import {
  ApplicationMilestone,
  FileUploadAttributes,
  TicketAttributes,
  TicketFormValidator,
  TicketPriority,
} from "@copetence/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import NotificationBanner from "components/banner/Banner.comp";
import { PriorityIndicator } from "components/priorityIndicator/PriorityIndicator";
import { TextController } from "components/textController/TextController.comp";
import { classes } from "handlers/StyleHandler";
import { useAppContext } from "providers/ContextProvider";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { API } from "../../../handlers/APIHandler";
import { useCreateTicketState } from "./CreateTicketState";
import s from "./createTicket.module.scss";

type TicketForm = z.infer<typeof TicketFormValidator> & { ticketCategoryId?: string };

const CreateTicket = () => {
  const state = useCreateTicketState();
  const setProblemDescription = useCreateTicketState(s => s.setProblemDescription);
  const { t } = useTranslation();
  const { appId, email, description, category, ref } = useAppContext();
  const { hideApplicationField, hideContactDetailsIfValid, hideFileUploads } = useAppContext();
  const { hidePrioritySelector, hideCategorySelector } = useAppContext();

  const uploadFile = async (file: File) => {
    try {
      // Step 1: Load the public key from the .crt file
      const publicKeyPem = await fetch("/ticketing.crt").then((res) =>
        res.text()
      );
      const publicKey = await importRsaKey(publicKeyPem);

      // Step 2: Generate a random AES key for file encryption
      const aesKey = await crypto.subtle.generateKey(
        {
          name: "AES-GCM",
          length: 256,
        },
        true,
        ["encrypt", "decrypt"]
      );

      // Step 3: Create an initialization vector (IV)
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Step 4: Read the file as ArrayBuffer and encrypt with AES
      const fileBuffer = await file.arrayBuffer();
      const encryptedFile = await crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: iv,
        },
        aesKey,
        fileBuffer
      );

      // Step 5: Export the AES key to raw format
      const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);

      // Step 6: Encrypt the AES key with the RSA public key
      const encryptedAesKey = await crypto.subtle.encrypt(
        {
          name: "RSA-OAEP",
        },
        publicKey,
        rawAesKey
      );

      // Step 7: Combine IV + encrypted key + encrypted file
      const encryptedKeyArray = new Uint8Array(encryptedAesKey);
      const combinedArray = new Uint8Array(
        iv.length + 4 + encryptedKeyArray.length + encryptedFile.byteLength
      );

      // Format: [12 bytes IV][4 bytes key length][encrypted key][encrypted file]
      combinedArray.set(iv, 0);
      const keyLengthView = new DataView(combinedArray.buffer, iv.length, 4);
      keyLengthView.setUint32(0, encryptedKeyArray.length, true);
      combinedArray.set(new Uint8Array(encryptedKeyArray), iv.length + 4);
      combinedArray.set(
        new Uint8Array(encryptedFile),
        iv.length + 4 + encryptedKeyArray.length
      );

      // Step 8: Create a Blob and upload
      const encryptedBlob = new Blob([combinedArray]);

      const fileBuffer2 = await encryptedBlob.arrayBuffer();

      const iv2 = new Uint8Array(fileBuffer2.slice(0, 12));

      // Extract the encrypted key length (4 bytes)
      const keyLengthView2 = new DataView(fileBuffer2.slice(12, 16));
      const encryptedKeyLength = keyLengthView2.getUint32(0, true);

      // Extract the encrypted key
      const encryptedKeyBuffer = fileBuffer2.slice(16, 16 + encryptedKeyLength);

      // Extract the encrypted file data
      const encryptedFile2 = new Uint8Array(
        fileBuffer2.slice(16 + encryptedKeyLength)
      );

      console.log("iv2", iv2);
      console.log("iv", iv);
      console.log("encryptedKeyBuffer", encryptedKeyBuffer);
      console.log("encryptedFile", encryptedFile2);

      // Step 9: Preserve the original file name
      const formData = new FormData();
      console.log("filename", file.name);
      formData.append("file", encryptedBlob, file.name);
      API.post<FileUploadAttributes>("/fileUploads/create", {
        body: formData,
      }).then((res) => {
        const currentFiles = useCreateTicketState.getState().uploadFiles;
        state.setUploadFiles([...currentFiles, res.data]);
      });
    } catch (err) {
      console.error("Encryption error:", err);
    }
  };

  /*
  Import a PEM encoded RSA private key, to use for RSA-PSS signing.
  Takes a string containing the PEM encoded key, and returns a Promise
  that will resolve to a CryptoKey representing the private key.
  */
  function importRsaKey(pem: string): Promise<CryptoKey> {
    // fetch the part of the PEM string between header and footer
    const pemHeader = "-----BEGIN PUBLIC KEY-----";
    const pemFooter = "-----END PUBLIC KEY-----";
    const pemContents = pem.substring(
      pemHeader.length,
      pem.length - pemFooter.length - 1
    );
    // base64 decode the string to get the binary data
    const binaryDerString = window.atob(pemContents);
    // convert from a binary string to an ArrayBuffer
    const binaryDer = str2ab(binaryDerString);

    return window.crypto.subtle.importKey(
      "spki",
      binaryDer,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      true,
      ["encrypt"]
    );
  }

  function str2ab(str: string): ArrayBuffer {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }
    return buf;
  }

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        uploadFile(file);
      }
    },
    [state.uploadFiles]
  );

  useEffect(() => {
    if (appId) {
      state.setApp(appId);
    }
  }, [appId]);

  const [success, setSuccess] = useState<boolean | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    clearErrors,
    reset,
    register,
  } = useForm<TicketForm>({
    defaultValues: {
      applicationId: state.app?.id || state.freeFormApp || "",
      issueDescription: state.problemDescription || "",
      priority: state.priority || TicketPriority.LOW,
      milestoneId: state.milestoneId || "Default Milestone",
      ticketCategoryId: state.ticketCategoryId || undefined,
      attachedFileIds: [],
      enquirerEmail: state.enquiries.email || "",
      enquirerPhone: state.enquiries.phone || "",
    },
    resolver: zodResolver(TicketFormValidator),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (state.app) {
      setValue("applicationId", state.app.id);
    } else if (state.freeFormApp) {
      setValue("applicationId", state.freeFormApp);
    }
  }, [state.app, state.freeFormApp, setValue]);

  useEffect(() => {
    if (email) {
      setValue("enquirerEmail", email);
      state.setEnquirieEmail(email);
    }
  }, [email, setValue]);

  useEffect(() => {
    if (description) {
      setValue("issueDescription", description);
      setProblemDescription(description);
    }
  }, [description, setValue, setProblemDescription]);

  useEffect(() => {
    if (ref) {
      useCreateTicketState.getState().setRef(ref);
    }
  }, [ref]);

  useEffect(() => {
    if (category && state.app?.ticketCategories?.length) {
      const valid = state.app.ticketCategories.some(c => c.id === category);
      if (valid) {
        setValue("ticketCategoryId", category);
        useCreateTicketState.getState().setTicketCategoryId(category);
      }
    }
  }, [category, setValue, state.app?.ticketCategories]);

  const onSubmit = (values: TicketForm) => {
    console.log("onSubmit values", values);
    const createTicketData: TicketForm = {
      applicationId: values.applicationId,
      issueDescription: values.issueDescription,
      priority: values.priority,
      milestoneId: state.milestoneId,
      ticketCategoryId: state.ticketCategoryId,
      attachedFileIds: state.uploadFiles.map((file) => file.id),
      ref: state.ref,
      enquirerEmail: values.enquirerEmail,
      enquirerPhone: values.enquirerPhone,
    };

    if (state.enquiries.email)
      createTicketData.enquirerEmail = state.enquiries.email;
    if (state.enquiries.phone)
      createTicketData.enquirerPhone = state.enquiries.phone;

    API.post<TicketAttributes>("/tickets/create", {
      body: createTicketData,
    })
      .then((res) => {
        console.log(res.data);
        // Clear state
        state.clear();
        reset();
        clearErrors();
        setSuccess(true);
      })
      .catch((err) => {
        console.error(err);
        setSuccess(false);
      });
  };

  useEffect(() => {
    setTimeout(() => {
      setSuccess(null);
    }, 5000);
  }, [success]);

  useEffect(() => {
    console.log("errorState", errorState);
  }, [errorState]);

  const onPaste = (e: any) => {
    for (let i = 0; i < e.clipboardData.items.length; i++) {
      const item = e.clipboardData.items[i];
      if (item.kind === "file") {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          uploadFile(blob);
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("paste", onPaste);
    };
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <>
      <div className={s.reportProblem}>
        <div className={s.container}>
          <div className={s.form}>
            <p
              className={s.subtitle}
              style={{ marginTop: 0, marginBottom: 20 }}
            >
              {t("tickets.yourProblem")}
            </p>

            <div className={s.inputGroup}>
              {/* App Name */}

              {!hideApplicationField &&
                (state.app ? (
                  <TextController
                    name="applicationId"
                    control={control}
                    label={t("tickets.application")}
                    type="text"
                    displayValue={state.app.name}
                    disabled
                    showRequiredIndicator
                  />
                ) : (
                  <TextController
                    name="applicationId"
                    control={control}
                    label={t("tickets.application")}
                    type="text"
                    placeholder={t("tickets.applicationPlaceholder")}
                    setValue={(value) => state.setFreeFormApp(value)}
                    showRequiredIndicator
                  />
                ))}

              {/* Problem Description */}

              <TextController
                showRequiredIndicator
                name="issueDescription"
                control={control}
                label={t("tickets.problemDescription")}
                type="text"
                asTextarea
                placeholder={t("tickets.problemPlaceholder")}
                setValue={(value) => state.setProblemDescription(value)}
              />
            </div>
            {!hideCategorySelector && state.app?.ticketCategories && state.app.ticketCategories.length > 0 && (
              <div>
                <p className={s.subtitle}>{t("tickets.category")} <span style={{ color: "var(--errorColor)" }}>*</span></p>
                <p className={s.blockDescription}>
                  {t("tickets.categoryHelp")}
                </p>
                <CategorySelector
                  categories={state.app.ticketCategories}
                  selectedCategoryId={state.ticketCategoryId}
                  setSelectedCategory={(categoryId) => {
                    useCreateTicketState.getState().setTicketCategoryId(categoryId);
                    setValue("ticketCategoryId", categoryId);
                  }}
                />
              </div>
            )}
            {!hideFileUploads && (
              <>
                <p className={s.subtitle}>{t("tickets.picturesAndFiles")}</p>
                <p className={s.blockDescription}>
                  {t("tickets.picturesAndFilesHelp")}
                </p>

                {(state.app?.id === "le_psi_spirc" ||
                  state.app?.id === "le_psi_porc") && (
                  <NotificationBanner
                    message={t("tickets.psiExcelWarning")}
                    type="warning"
                  />
                )}

                {state.uploadFiles.length > 0 && (
                  <div className={s.filesContainer}>
                    {state.uploadFiles.map((file, i) => (
                      <FileBar
                        key={i}
                        file={file}
                        onRemove={() => state.removeUploadFile(file.id)}
                      />
                    ))}
                  </div>
                )}

                <div className={s.dropzone} {...getRootProps()}>
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p>{t("tickets.dropHere")}</p>
                  ) : (
                    <p>{t("tickets.dragDrop")}</p>
                  )}
                </div>
              </>
            )}

            {!hidePrioritySelector && (
              <>
                <p className={s.subtitle}>{t("tickets.priority")}</p>
                <p className={s.blockDescription}>{t("tickets.priorityHelp")}</p>
                <div className={s.prioritySelector}>
                  <PriorityOption
                    label={t("tickets.priorityLow")}
                    value={TicketPriority.LOW}
                    selected={state.priority === TicketPriority.LOW}
                    onClick={() => {
                      state.setPriority(TicketPriority.LOW);
                      setValue("priority", TicketPriority.LOW);
                    }}
                  />
                  <PriorityOption
                    label={t("tickets.priorityMedium")}
                    value={TicketPriority.MEDIUM}
                    selected={state.priority === TicketPriority.MEDIUM}
                    onClick={() => {
                      state.setPriority(TicketPriority.MEDIUM);
                      setValue("priority", TicketPriority.MEDIUM);
                    }}
                  />
                  <PriorityOption
                    label={t("tickets.priorityHigh")}
                    value={TicketPriority.HIGH}
                    selected={state.priority === TicketPriority.HIGH}
                    onClick={() => {
                      state.setPriority(TicketPriority.HIGH);
                      setValue("priority", TicketPriority.HIGH);
                    }}
                  />
                  <PriorityOption
                    label={t("tickets.priorityUrgent", "Urgent")}
                    value={TicketPriority.URGENT}
                    selected={state.priority === TicketPriority.URGENT}
                    onClick={() => {
                      state.setPriority(TicketPriority.URGENT);
                      setValue("priority", TicketPriority.URGENT);
                    }}
                  />
                </div>
              </>
            )}

            {state.app?.milestones && state.app.milestones.length > 0 && (
              <div>
                <p className={s.subtitle}>{t("tickets.milestone")}</p>
                <p className={s.blockDescription}>
                  {t("tickets.milestoneHelp")}
                </p>
                <MilestoneSelector
                  milestones={state.app.milestones}
                  selectedMilestone={state.milestoneId}
                  setSelectedMilestone={
                    (milestoneId) => {
                      state.setMilestoneId(milestoneId);
                      setValue("milestoneId", milestoneId);
                    }
                  }
                />
              </div>
            )}

            {(!hideContactDetailsIfValid || !email) && (
              <>
                <p className={s.subtitle}>{t("tickets.contactDetails")}</p>
                <p className={s.blockDescription}>
                  {t("tickets.contactDetailsHelp")}
                </p>
                <div className={s.inputGroup}>
                  <TextController
                    name="enquirerEmail"
                    control={control}
                    label={t("tickets.email")}
                    type="email"
                    placeholder={t("tickets.emailPlaceholder")}
                    setValue={(value) => state.setEnquirieEmail(value)}
                  />
                  {/* <TextController
                                      name="enquirerPhone"
                                      control={control}
                                      label="Phone"
                                      type="tel"
                                      placeholder="Your phone number"
                                      setValue={(value) => state.setEnquiriePhone(value)}
                                  /> */}
                </div>
              </>
            )}

            {success != null ? (
              success ? (
                <NotificationBanner
                  message={t("tickets.success")}
                  type="success"
                />
              ) : (
                <NotificationBanner message={t("tickets.error")} type="error" />
              )
            ) : null}

            <button
              className={s.submitBTN}
              onClick={() => {
                handleSubmit(
                  (values) => {
                    console.log("handleSubmit values", values);
                    onSubmit(values);
                  },
                  (errors) => {
                    console.log("errors", errors);
                  }
                )();
              }}
            >
              {t("tickets.submit")}
            </button>
            <div className={s.errorText}>
              <p>{errorState}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PriorityOption = ({
  label,
  value,
  selected,
  onClick,
}: {
  label: string;
  value: TicketPriority;
  selected: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      className={classes(s.priorityOption, selected ? s.selected : "")}
      onClick={onClick}
    >
      <PriorityIndicator priority={value} onAccent={selected} />
      <p>{label}</p>
    </div>
  );
};

const CategorySelector = ({
  categories,
  selectedCategoryId,
  setSelectedCategory,
}: {
  categories: { id: string; name: string; description?: string }[];
  selectedCategoryId?: string;
  setSelectedCategory: (categoryId?: string) => void;
}) => {
  const [selectorOpen, setSelectorOpen] = useState<boolean>(false);
  useEffect(() => {
    const handleClick = (e: any) => {
      if (e.target.closest(`.${s.milestoneSelector}`)) return;
      setSelectorOpen(false);
    };
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <div
      className={classes(s.milestoneSelector, selectorOpen ? s.open : "")}
      onClick={() => setSelectorOpen(!selectorOpen)}
    >
      <div className={s.selectedOption}>
        <div className={s.text}>
          <p>
            {selectedCategoryId
              ? categories.find((c) => c.id === selectedCategoryId)?.name
              : "Select Category"}
          </p>
          <p className={s.description}>
            {selectedCategoryId
              ? categories.find((c) => c.id === selectedCategoryId)?.description
              : "/"}
          </p>
        </div>
        <p className={s.openIndicator}>▼</p>
      </div>
      {selectorOpen ? (
        <div className={s.options}>
          {categories
            .filter((c) => c.id !== selectedCategoryId)
            .map((category, i) => (
              <div
                key={i}
                className={classes(
                  s.option,
                  selectedCategoryId === category.id ? s.selected : ""
                )}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setSelectorOpen(false);
                }}
              >
                <p>{category.name}</p>
                <p className={s.description}>{category.description}</p>
              </div>
            ))}
        </div>
      ) : null}
    </div>
  );
};

const MilestoneSelector = ({
  milestones,
  selectedMilestone,
  setSelectedMilestone,
}: {
  milestones: ApplicationMilestone[];
  selectedMilestone: string | null;
  setSelectedMilestone: (milestoneId: string) => void;
}) => {
  const [selectorOpen, setSelectorOpen] = useState<boolean>(false);

  //close if clicked outside but check if the click was on the selector
  useEffect(() => {
    const handleClick = (e: any) => {
      if (e.target.closest(`.${s.milestoneSelector}`)) return;
      setSelectorOpen(false);
    };

    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <div
      className={classes(s.milestoneSelector, selectorOpen ? s.open : "")}
      onClick={() => setSelectorOpen(!selectorOpen)}
    >
      <div className={s.selectedOption}>
        <div className={s.text}>
          <p>
            {selectedMilestone
              ? milestones.find((m) => m.id === selectedMilestone)?.name
              : "Select Milestone"}
          </p>
          <p className={s.description}>
            {selectedMilestone
              ? milestones.find((m) => m.id === selectedMilestone)?.description
              : "/"}
          </p>
        </div>
        <p className={s.openIndicator}>▼</p>
      </div>
      {selectorOpen ? (
        <div className={s.options}>
          {milestones
            .filter((milestone) => milestone.id !== selectedMilestone)
            .map((milestone, i) => (
              <div
                key={i}
                className={classes(
                  s.option,
                  selectedMilestone === milestone.id ? s.selected : ""
                )}
                onClick={() => {
                  setSelectedMilestone(milestone.id);
                  setSelectorOpen(false);
                }}
              >
                <p>{milestone.name}</p>
                <p className={s.description}>{milestone.description}</p>
              </div>
            ))}
        </div>
      ) : null}
    </div>
  );
};

const FileBar = ({
  file,
  onRemove,
}: {
  file: FileUploadAttributes;
  onRemove: () => void;
}) => {
  console.log("file", file);
  return (
    <div className={s.fileBar}>
      <p className={s.fileName}>{file.fileName}</p>
      <div className={s.buttons}>
        <button className={s.removeBTN} onClick={onRemove}>
          Remove
        </button>
      </div>
    </div>
  );
};

export default CreateTicket;
